import { useRef, useState } from "react";
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, ArrowUpTrayIcon, CheckIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, ConfirmModal, Input, RadioGroup, Tag, Textarea } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf } from "../../../api/scholarships";
import { AMOUNTS, formatMoney } from "../funding";
import { fileSummary, formatDateTime, formatLocalDateTime } from "../review/model";
import { formatKigaliDate as formatDate, kigaliDate } from "../model";
import { paymentKindOf } from "../brd/model";

const TRAVEL_FIELDS = ["departureAirport", "arrivalAirport", "preferredTravelDate", "passportNumber", "passportExpiry"];
const VISA_TYPES = "application/pdf,image/jpeg,image/png";
const MAX_REASON = 500;

const VISA_OPTIONS = [
  { value: "yes", label: "Yes, I need a visa" },
  { value: "no", label: "No, my Rwandan passport is enough" },
];

const travelFormOf = (travel) => ({
  ...Object.fromEntries(TRAVEL_FIELDS.map((key) => [key, travel?.[key] ?? ""])),
  visaRequired: travel?.visaRequired == null ? "" : travel.visaRequired ? "yes" : "no",
});
const travelBodyOf = (form) => ({
  ...Object.fromEntries(TRAVEL_FIELDS.map((key) => [key, form[key].trim() || null])),
  visaRequired: form.visaRequired === "" ? null : form.visaRequired === "yes",
});
const today = () => kigaliDate(new Date().toISOString());

/**
 * The loan the Minister approved: the amounts, the approval letter, and the student's answer.
 * `actions` makes the calls on the API the loan lives on (`respond(body)`, `openLetter()`) and keeps
 * the answer. `travels` is true for a loan abroad, whose allowances go through the embassy.
 */
export function LoanCard({ applicationId, loan, travels, actions }) {
  const [answering, setAnswering] = useState(null); // "accept" or "decline"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const { approvedFunding: funding, letter, response } = loan;

  const respond = async () => {
    setBusy(true);
    const accept = answering === "accept";
    try {
      await actions.respond({ accept, ...(!accept && reason.trim() && { reason: reason.trim() }) });
      toast.success(accept ? "Loan accepted" : "Loan declined");
      setAnswering(null);
    } catch (error) {
      console.error(`Failed to ${answering} the loan of application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to save your answer. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Your loan">
      <div className="flex flex-col gap-4">
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {AMOUNTS.filter(({ key }) => funding?.[key] != null).map(({ key, label }) => (
            <div key={key} className="flex justify-between gap-4 border-b border-slate-100 pb-1">
              <dt className="text-slate-500">{label}</dt>
              <dd className="font-medium text-slate-800">{formatMoney(funding[key], funding.currency)}</dd>
            </div>
          ))}
        </dl>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 px-4 py-3 text-sm">
          <span className="text-slate-700">
            Approval letter <span className="font-medium text-slate-800">{letter.reference}</span>
            {letter.issuedAt && ` · ${formatDateTime(letter.issuedAt)}`}
          </span>
          <Button size="sm" variant="outline" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={actions.openLetter}>
            Download letter
          </Button>
        </div>
        {loan.payments?.length > 0 && (
          <div className="text-sm">
            <p className="mb-1 font-medium text-slate-800">Paid by BRD</p>
            <ul className="flex flex-col gap-1 text-slate-700">
              {loan.payments.map((p) => (
                <li key={p.id} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-1">
                  <span>{travels && p.kind === "ALLOWANCE" ? `Allowance sent to the ${p.embassy ?? "embassy"}` : paymentKindOf(p.kind, travels)}</span>
                  <span>
                    <span className="font-medium text-slate-800">{formatMoney(p.amount, p.currency)}</span> · {formatDate(p.paidOn)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {response ? (
          <p className="text-sm text-slate-700">
            <Tag color={response.accepted ? "green" : "red"}>{response.accepted ? "Accepted" : "Declined"}</Tag> <span className="ml-1">{formatDateTime(response.at)}</span>
            {response.reason && <span className="ml-2 italic">“{response.reason}”</span>}
          </p>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="danger-soft" onClick={() => setAnswering("decline")}>
              Decline
            </Button>
            <Button icon={<CheckIcon className="h-4 w-4" />} onClick={() => setAnswering("accept")}>
              Accept the loan
            </Button>
          </div>
        )}
      </div>
      <ConfirmModal
        open={Boolean(answering)}
        onClose={() => setAnswering(null)}
        onConfirm={respond}
        loading={busy}
        destructive={answering === "decline"}
        title={answering === "accept" ? "Accept the loan?" : "Decline the loan?"}
        description={answering === "accept" ? "You accept the loan on the terms of the approval letter." : "The loan will go to another student. This cannot be undone."}
        confirmLabel={answering === "accept" ? "Accept" : "Decline"}
      >
        {answering === "decline" && <Textarea label="Reason (optional)" rows={2} maxLength={MAX_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </ConfirmModal>
    </Card>
  );
}

// The travel details BRD needs to buy the ticket, and the visa when the country of study asks for one:
// BRD buys the ticket only once a required visa is uploaded. `actions` as for LoanCard: `save(body)`,
// `submit()`, `uploadVisa(file)`, `openVisa()` and `openTicket()`
export function TravelCard({ applicationId, travel, editable, actions }) {
  const [form, setForm] = useState(() => travelFormOf(travel));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null); // "save", "send" or "visa"
  const visaInput = useRef(null);
  const needsVisa = form.visaRequired === "yes";
  const changed = JSON.stringify(travelBodyOf(form)) !== JSON.stringify(travelBodyOf(travelFormOf(travel)));

  const call = async (kind, work, success) => {
    setBusy(kind);
    try {
      await work();
      if (success) toast.success(success);
      return true;
    } catch (error) {
      console.error(`Failed to ${kind} the travel details of application ${applicationId}:`, error);
      setErrors((prev) => ({ ...prev, ...fieldErrorsOf(problemOf(error)) }));
      toast.error(errorMessageOf(error, "Failed to save. Please try again."));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const save = (success = "Travel details saved") => call("save", () => actions.save(travelBodyOf(form)), success);
  const sendToBrd = async () => {
    setErrors({});
    if (changed && !(await save(null))) return;
    await call("send", () => actions.submit(), "Travel details sent to BRD");
  };
  const uploadVisa = (file) => {
    setErrors((prev) => ({ ...prev, file: undefined }));
    call("visa", () => actions.uploadVisa(file), "Visa uploaded");
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: !editable || Boolean(busy),
  });

  return (
    <Card title="Travel details" actions={travel.submittedAt && <Tag color="green">Sent to BRD {formatDateTime(travel.submittedAt)}</Tag>}>
      {travel.ticket && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
          <span>
            Your ticket:{" "}
            <span className="font-medium">
              {travel.ticket.airline} {travel.ticket.flightNumber}
            </span>
            , departs {formatLocalDateTime(travel.ticket.departureAt)}
          </span>
          <Button size="sm" variant="outline" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={actions.openTicket}>
            Download ticket
          </Button>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Input label="Departure airport" required maxLength={100} {...field("departureAirport")} />
        <Input label="Arrival airport" required maxLength={100} {...field("arrivalAirport")} />
        <Input type="date" label="Preferred travel date" required min={today()} {...field("preferredTravelDate")} />
        <Input label="Passport number" required maxLength={12} {...field("passportNumber")} />
        <Input type="date" label="Passport expiry date" required min={form.preferredTravelDate || today()} {...field("passportExpiry")} />
        <RadioGroup
          className="md:col-span-2"
          label="Does your country of study require a visa for a Rwandan passport?"
          name="visaRequired"
          required
          options={VISA_OPTIONS}
          {...field("visaRequired")}
        />
      </div>

      {(needsVisa || travel.visa) && (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md bg-slate-50 px-4 py-3 text-sm">
        <span className="text-slate-700">
          Visa {travel.visa ? <span className="font-medium text-slate-800">{fileSummary(travel.visa)}</span> : <span className="text-slate-500">(upload it once you have it)</span>}
        </span>
        <div className="flex items-center gap-2">
          {travel.visa && (
            <Button size="sm" variant="ghost" icon={<ArrowTopRightOnSquareIcon className="h-4 w-4" />} onClick={actions.openVisa}>
              Open
            </Button>
          )}
          {editable && (
            <>
              <Button size="sm" variant="outline" icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => visaInput.current?.click()} loading={busy === "visa"}>
                {travel.visa ? "Replace" : "Upload"}
              </Button>
              <input
                ref={visaInput}
                type="file"
                accept={VISA_TYPES}
                className="hidden"
                aria-label="Visa file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) uploadVisa(file);
                }}
              />
            </>
          )}
        </div>
      </div>
      )}
      {errors.file && <p className="mt-1 text-sm text-red-600">{errors.file}</p>}
      {travel.visaRequired && !travel.visa && (
        <p className="mt-2 text-sm text-amber-700">BRD buys your ticket only once you have uploaded your visa.</p>
      )}

      {editable && (
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => save()} loading={busy === "save"} disabled={!changed || Boolean(busy)} icon={<CheckIcon className="h-4 w-4" />}>
            Save
          </Button>
          {(!travel.submittedAt || changed) && (
            <Button onClick={sendToBrd} loading={busy === "send"} disabled={Boolean(busy)} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
              Send to BRD
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
